import { useMemo } from "react";
import { ArrowUpRight, CalendarDays, CirclePlay, Clock3 } from "lucide-react";
import { Link } from "@tanstack/react-router";

import { AutoLinkText } from "@/components/ui/auto-link-text";
import { PODCAST_CONFIG } from "../podcast-config";
import { isCountedPodcastEpisode, podcastEpisodeSlug, type PodcastFeedState } from "../podcast-rss";
import type { PodcastEpisode } from "../types";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("es-CO", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function episodeLabel(episode: PodcastEpisode) {
  if (episode.episodeType === "trailer") return "Tráiler";
  if (episode.episodeType === "bonus") return "Extra";
  return episode.episode ? `Episodio ${String(episode.episode).padStart(2, "0")}` : "Episodio";
}

export function PodcastEpisodeArchive({ archive }: { archive: PodcastFeedState }) {
  const seasons = useMemo(() => {
    const grouped = new Map<number, PodcastEpisode[]>();
    for (const episode of archive.episodes) {
      const season = episode.season ?? 1;
      grouped.set(season, [...(grouped.get(season) ?? []), episode]);
    }
    return Array.from(grouped.entries()).sort(([first], [second]) => second - first);
  }, [archive.episodes]);
  const episodeCount = archive.episodes.filter(isCountedPodcastEpisode).length;

  return (
    <section aria-labelledby="podcast-episodes-title" className="border-t border-black/10 bg-white">
      <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 sm:py-5">
        <div className="flex items-end justify-between gap-6 border-b border-black pb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-black/60">
              Biblioteca del pódcast
            </p>
            <h2
              id="podcast-episodes-title"
              className="mt-2 text-3xl font-semibold tracking-[-0.05em] sm:text-4xl"
            >
              Todos los episodios
            </h2>
            <p>
              Haz clic en el título del episodio para ver más opciones. Usa la flecha para
              escucharlo en tu plataforma de pódcast.
            </p>
          </div>
          {archive.status === "ready" && (
            <p className="shrink-0 text-xs text-black/45">
              {episodeCount} episodio{episodeCount === 1 ? "" : "s"}
            </p>
          )}
        </div>

        {archive.status === "loading" && (
          <div className="flex items-center gap-3 py-5 text-sm text-black/50" role="status">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-black/15 border-t-black" />
            Cargando episodios…
          </div>
        )}

        {archive.status === "error" && (
          <div className="mt-4 flex flex-col justify-between gap-5 border border-black/15 p-5 sm:flex-row sm:items-center">
            <div>
              <p className="text-sm font-semibold">No pudimos cargar los episodios ahora.</p>
              <p className="mt-1 text-xs text-black/50">
                Puedes escucharlos desde tu plataforma favorita.
              </p>
            </div>
            {PODCAST_CONFIG.platforms[0]?.url && (
              <a
                href={PODCAST_CONFIG.platforms[0].url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-black px-4 text-xs font-semibold text-white"
              >
                Abrir el pódcast
                <ArrowUpRight className="h-4 w-4" />
              </a>
            )}
          </div>
        )}

        {archive.status === "ready" && archive.episodes.length === 0 && (
          <p className="py-12 text-sm text-black/50">Todavía no hay episodios publicados.</p>
        )}

        <div>
          {seasons.map(([season, episodes]) => (
            <section
              key={season}
              aria-labelledby={`podcast-season-${season}`}
              className="grid border-b border-black/15 md:grid-cols-[190px_minmax(0,1fr)]"
            >
              <div className="py-6 md:border-r md:border-black/15 md:pr-6">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-black/60">
                  Temporada
                </p>
                <h3
                  id={`podcast-season-${season}`}
                  className="mt-1 text-xl font-semibold tracking-[-0.04em]"
                >
                  {String(season).padStart(2, "0")}
                </h3>
                <p className="mt-1 text-xs text-black/40">
                  {episodes.filter(isCountedPodcastEpisode).length} episodio
                  {episodes.filter(isCountedPodcastEpisode).length === 1 ? "" : "s"}
                </p>
                {season === 1 && (
                  <p className="mt-3 text-xs leading-5 text-black/55">
                    {PODCAST_CONFIG.seasonOneTagline}
                  </p>
                )}
              </div>

              <div className="divide-y divide-black/10 md:pl-7">
                {episodes.map((episode) => {
                  const editorialEpisode = PODCAST_CONFIG.episodes.find(
                    (configuredEpisode) => configuredEpisode.id === episode.id,
                  );
                  const internalSlug = editorialEpisode?.slug ?? podcastEpisodeSlug(episode);
                  return (
                    <article key={episode.id} className="py-6 first:pt-6">
                      <div className="flex gap-4 sm:gap-5">
                        <a
                          href={episode.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-black text-white transition hover:scale-105"
                          aria-label={`Escuchar ${episode.title} en Spotify`}
                        >
                          <CirclePlay className="h-5 w-5" />
                        </a>

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-black/60">
                            <span className="font-semibold uppercase tracking-[0.14em]">
                              {episodeLabel(episode)}
                            </span>
                            <span className="flex items-center gap-1">
                              <CalendarDays className="h-3 w-3" />
                              {formatDate(episode.publishedAt)}
                            </span>
                            {episode.duration && (
                              <span className="flex items-center gap-1">
                                <Clock3 className="h-3 w-3" />
                                {episode.duration}
                              </span>
                            )}
                          </div>

                          <div className="mt-2 flex items-start justify-between gap-4">
                            <div>
                              <h4 className="text-base font-semibold leading-snug">
                                <Link
                                  to="/podcast/$episodeSlug"
                                  params={{ episodeSlug: internalSlug }}
                                  className="hover:underline hover:underline-offset-4"
                                >
                                  {episode.title}
                                </Link>
                              </h4>
                              {episode.description && (
                                <p className="mt-1 line-clamp-2 max-w-3xl text-xs leading-5 text-black/50">
                                  <AutoLinkText text={episode.description} />
                                </p>
                              )}
                            </div>
                            <a
                              href={episode.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`Abrir ${episode.title} en la plataforma del pódcast`}
                              className="shrink-0 text-black/45 transition hover:text-black"
                            >
                              <ArrowUpRight className="h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </section>
  );
}
